import React, { useEffect, useRef, useState } from "react"
import { Button, Card, Col, ProgressBar, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import API from "../../common/API"

type ImportStage = "Queued" | "BulkLoading" | "Completed" | "Failed" | "Cancelled"

interface ImportStatus {
  jobId: string
  fileName: string
  fileSizeMB: number
  stage: ImportStage
  message?: string
  errorMessage?: string
  rowsProcessed: number
  megabytesProcessed: number
  progressPercent: number
  currentLine: number
  rowsPerSecond: number
  megabytesPerSecond: number
  skippedRows: number
  elapsedSeconds: number
  etaSeconds?: number | null
  startedAt: string
  finishedAt?: string | null
}

interface DryRunResponse {
  fileName: string
  fileSizeMB: number
  linesScanned: number
  sampleRows: Record<string, string>[]
  anomalies: {
    wrongColumnCount: number
    invalidCedula: number
    invalidFechaSalario: number
    invalidDecimalSalarioISR: number
  }
  distinctFechaSalarioInSample: string[]
}

const formatSeconds = (s?: number | null) => {
  if (s == null || !isFinite(s)) return "—"
  const sec = Math.max(0, Math.round(s))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const r = sec % 60
  if (h > 0) return `${h}h ${m}m ${r}s`
  if (m > 0) return `${m}m ${r}s`
  return `${r}s`
}

const stageVariant = (stage: ImportStage) => {
  switch (stage) {
    case "Completed":
      return "success"
    case "Failed":
      return "danger"
    case "Cancelled":
      return "warning"
    case "BulkLoading":
      return "info"
    default:
      return "secondary"
  }
}

const isTerminal = (stage: ImportStage) =>
  stage === "Completed" || stage === "Failed" || stage === "Cancelled"

const AdminSalariosComponent: React.FC = () => {
  const { t } = useTranslation("global")
  const [fileName, setFileName] = useState("personas.csv")
  const [separator, setSeparator] = useState<"tab" | "comma" | "semicolon" | "pipe">("comma")
  const [hasHeader, setHasHeader] = useState(false)
  const [batchSize, setBatchSize] = useState(100000)
  const [startFromLine, setStartFromLine] = useState(0)
  const [isStarting, setIsStarting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isDryRunning, setIsDryRunning] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [isCountingLines, setIsCountingLines] = useState(false)
  const [status, setStatus] = useState<ImportStatus | null>(null)
  const [dryRun, setDryRun] = useState<DryRunResponse | null>(null)
  const [lineCount, setLineCount] = useState<{ totalLines: number; dataRows: number; fileSizeMB: number; elapsedMs: number } | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  const pollOnce = async (jobId: string) => {
    try {
      const res = await API.getAction(`Salario/ImportStatus/${jobId}`)
      if (res.data.status === "ok") {
        const s = res.data.response as ImportStatus
        setStatus(s)
        if (isTerminal(s.stage)) {
          stopPolling()
          if (s.stage === "Completed") toast.success(t("Importación completada"))
          else if (s.stage === "Failed") toast.error(t("La importación falló"))
          else if (s.stage === "Cancelled") toast.warning(t("Importación cancelada"))
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const startPolling = (jobId: string) => {
    stopPolling()
    pollOnce(jobId)
    pollTimerRef.current = setInterval(() => pollOnce(jobId), 3000)
  }

  useEffect(() => {
    return () => stopPolling()
  }, [])

  const handleCountLines = async () => {
    try {
      setIsCountingLines(true)
      setLineCount(null)
      const res = await API.getAction(
        `Salario/CountLines?fileName=${encodeURIComponent(fileName)}&hasHeader=${hasHeader}`
      )
      if (res.data.status === "ok") {
        setLineCount(res.data.response)
        toast.success(t("Conteo completado"))
      } else {
        toast.error(res.data.response ?? t("Error al contar líneas"))
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error al contar líneas"))
    } finally {
      setIsCountingLines(false)
    }
  }

  const handleDryRun = async () => {
    try {
      setIsDryRunning(true)
      setDryRun(null)
      const res = await API.getAction(
        `Salario/ImportSalariosDryRun?fileName=${encodeURIComponent(fileName)}` +
        `&separator=${separator}&hasHeader=${hasHeader}&rows=10000`
      )
      if (res.data.status === "ok") {
        setDryRun(res.data.response as DryRunResponse)
        toast.success(t("Validación de archivo completada"))
      } else {
        toast.error(res.data.response ?? t("Error en validación"))
      }
    } catch (e: any) {
      console.error(e)
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error en validación"))
    } finally {
      setIsDryRunning(false)
    }
  }

  const handleStart = async (force = false) => {
    if (
      !window.confirm(
        String(
          t(
            "Se importará el archivo completo a la tabla Salario. Los duplicados por (EmpleadorID, IdEmpleado, FechaSalario) se descartan automáticamente por el índice UX_Salario_Dedup."
          )
        )
      )
    )
      return

    try {
      setIsStarting(true)
      const params =
        `fileName=${encodeURIComponent(fileName)}` +
        `&separator=${separator}` +
        `&hasHeader=${hasHeader}` +
        `&batchSize=${batchSize}` +
        `&startFromLine=${startFromLine}` +
        `&force=${force}`
      const res = await API.postAction(`Salario/ImportSalariosFast?${params}`)
      if (res.data.status === "ok") {
        const jobId = res.data.response.jobId as string
        toast.success(t("Importación iniciada"))
        const initialStatus: ImportStatus = {
          jobId,
          fileName,
          fileSizeMB: res.data.response.fileSizeMB,
          stage: "BulkLoading",
          rowsProcessed: 0,
          megabytesProcessed: 0,
          progressPercent: 0,
          currentLine: startFromLine,
          rowsPerSecond: 0,
          megabytesPerSecond: 0,
          elapsedSeconds: 0,
          startedAt: res.data.response.startedAt,
          skippedRows: 0,
        }
        setStatus(initialStatus)
        startPolling(jobId)
      } else {
        toast.error(res.data.response ?? t("Error al iniciar"))
      }
    } catch (e: any) {
      console.error(e)
      const msg = e?.response?.data?.response
      if (e?.response?.status === 409) {
        if (
          window.confirm(
            String(
              t(
                "Ya hay un job corriendo (o zombi). ¿Forzar? Esto ignora el job previo y arranca uno nuevo."
              )
            )
          )
        ) {
          handleStart(true)
          return
        }
      }
      toast.error(msg ?? e.message ?? t("Error al iniciar"))
    } finally {
      setIsStarting(false)
    }
  }

  const handleClearJobs = async () => {
    try {
      setIsClearing(true)
      const res = await API.postAction("Salario/ImportClearJobs")
      if (res.data.status === "ok") {
        toast.info(
          `${t("Jobs limpiados")}: ${res.data.response.removed ?? 0}`
        )
      }
    } catch (e: any) {
      console.error(e)
      toast.error(e?.message ?? t("Error al limpiar jobs"))
    } finally {
      setIsClearing(false)
    }
  }

  const handleCancel = async () => {
    if (!status?.jobId) return
    if (!window.confirm(String(t("¿Cancelar la importación en curso?")))) return
    try {
      setIsCancelling(true)
      const res = await API.postAction(`Salario/ImportCancel/${status.jobId}`)
      if (res.data.status === "ok") {
        toast.info(t("Cancelación solicitada"))
      }
    } catch (e: any) {
      console.error(e)
      toast.error(e?.message ?? t("Error al cancelar"))
    } finally {
      setIsCancelling(false)
    }
  }

  const isRunning = status != null && !isTerminal(status.stage)

  return (
    <div className="my-4">
      <h4 className="mb-3">
        <i className="fas fa-coins me-2" />
        {t("Importar Salarios")}
      </h4>

      <div className="alert alert-info">
        <i className="fas fa-info-circle me-2" />
        {t(
          "Streaming directo a Salario, sin staging. El índice UX_Salario_Dedup (IGNORE_DUP_KEY = ON) descarta duplicados en ingest. Cada batch se commitea independiente: si algo falla, puedes reanudar desde la línea indicada. Antes de la primera corrida, ejecuta el DDL al final de database.sql."
        )}
      </div>

      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>{t("1. Configuración")}</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={5}>
              <label className="form-label">{t("Nombre del archivo")}</label>
              <input
                type="text"
                className="form-control"
                value={fileName}
                onChange={e => setFileName(e.target.value)}
                disabled={isRunning}
              />
              <small className="text-muted">
                {t("Debe estar en wwwroot/supportDocuments del API")}
              </small>
            </Col>
            <Col md={2}>
              <label className="form-label">{t("Separador")}</label>
              <select
                className="form-control"
                value={separator}
                onChange={e => setSeparator(e.target.value as any)}
                disabled={isRunning}
              >
                <option value="comma">Coma</option>
                <option value="tab">Tab</option>
                <option value="semicolon">Punto y coma</option>
                <option value="pipe">Pipe</option>
              </select>
            </Col>
            <Col md={2}>
              <label className="form-label">{t("Tiene header")}</label>
              <select
                className="form-control"
                value={hasHeader ? "yes" : "no"}
                onChange={e => setHasHeader(e.target.value === "yes")}
                disabled={isRunning}
              >
                <option value="no">{t("No")}</option>
                <option value="yes">{t("Sí")}</option>
              </select>
            </Col>
            <Col md={3}>
              <label className="form-label">&nbsp;</label>
              <Button
                variant="outline-secondary"
                className="d-block w-100 mb-2"
                onClick={handleCountLines}
                disabled={isCountingLines || isRunning}
              >
                {isCountingLines ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t("Contando...")}
                  </>
                ) : (
                  <>
                    <i className="fas fa-hashtag me-2" />
                    {t("Contar líneas")}
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                className="d-block w-100"
                onClick={handleDryRun}
                disabled={isDryRunning || isRunning}
              >
                {isDryRunning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t("Validando...")}
                  </>
                ) : (
                  <>
                    <i className="fas fa-vial me-2" />
                    {t("Validar archivo (Dry Run)")}
                  </>
                )}
              </Button>
            </Col>
          </Row>
          <Row className="g-3 mt-1">
            <Col md={3}>
              <label className="form-label">{t("Tamaño de batch")}</label>
              <input
                type="number"
                className="form-control"
                value={batchSize}
                onChange={e => setBatchSize(Number(e.target.value) || 100000)}
                min={10000}
                max={1000000}
                step={10000}
                disabled={isRunning}
              />
              <small className="text-muted">{t("Recomendado: 100000")}</small>
            </Col>
            <Col md={3}>
              <label className="form-label">{t("Reanudar desde línea")}</label>
              <input
                type="number"
                className="form-control"
                value={startFromLine}
                onChange={e => setStartFromLine(Math.max(0, Number(e.target.value) || 0))}
                min={0}
                step={100000}
                disabled={isRunning}
              />
              <small className="text-muted">{t("0 = inicio del archivo")}</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {lineCount && (
        <div className="alert alert-secondary d-flex flex-wrap gap-4 align-items-center mb-3">
          <span>
            <i className="fas fa-hashtag me-2 text-primary" />
            <strong>{t("Total líneas")}:</strong>{" "}
            <span className="fs-5 fw-bold">{lineCount.totalLines.toLocaleString()}</span>
          </span>
          <span>
            <strong>{t("Filas de datos")}:</strong>{" "}
            <span className="fs-5 fw-bold text-success">{lineCount.dataRows.toLocaleString()}</span>
          </span>
          <span>
            <strong>{t("Tamaño")}:</strong> {lineCount.fileSizeMB.toLocaleString()} MB
          </span>
          <span className="text-muted">
            <i className="fas fa-clock me-1" />
            {lineCount.elapsedMs.toLocaleString()} ms
          </span>
        </div>
      )}

      {dryRun && (
        <Card className="mb-3">
          <Card.Header className="bg-light">
            <strong>{t("2. Resultado de validación")}</strong>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={3}>
                <small className="text-muted d-block">{t("Tamaño")}</small>
                <strong>{dryRun.fileSizeMB.toLocaleString()} MB</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">{t("Filas escaneadas")}</small>
                <strong>{dryRun.linesScanned.toLocaleString()}</strong>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("Períodos en muestra")}
                </small>
                <strong>{dryRun.distinctFechaSalarioInSample.join(", ") || "—"}</strong>
              </Col>
            </Row>

            <h6>{t("Anomalías")}</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                <tr>
                  <td>{t("Filas con columnas ≠ 11")}</td>
                  <td className="text-end">
                    <span
                      className={
                        dryRun.anomalies.wrongColumnCount > 0 ? "text-danger" : "text-success"
                      }
                    >
                      {dryRun.anomalies.wrongColumnCount.toLocaleString()}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>{t("Cédulas inválidas")}</td>
                  <td className="text-end">
                    {dryRun.anomalies.invalidCedula.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>{t("Fechas inválidas")}</td>
                  <td className="text-end">
                    {dryRun.anomalies.invalidFechaSalario.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>{t("Decimales inválidos en SalarioISR")}</td>
                  <td className="text-end">
                    {dryRun.anomalies.invalidDecimalSalarioISR.toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </Table>

            <h6>{t("Primeras filas")}</h6>
            <div style={{ overflowX: "auto" }}>
              <Table size="sm" bordered striped className="small mb-0">
                <thead>
                  <tr>
                    {dryRun.sampleRows[0] &&
                      Object.keys(dryRun.sampleRows[0]).map(k => <th key={k}>{k}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {dryRun.sampleRows.map((r, idx) => (
                    <tr key={idx}>
                      {Object.values(r).map((v, i) => (
                        <td key={i}>{v ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>{t("3. Importación")}</strong>
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="primary"
              size="lg"
              onClick={() => handleStart(false)}
              disabled={isStarting || isRunning}
            >
              {isStarting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {t("Iniciando...")}
                </>
              ) : (
                <>
                  <i className="fas fa-play me-2" />
                  {t("Iniciar importación")}
                </>
              )}
            </Button>

            {isRunning && (
              <Button
                variant="outline-danger"
                size="lg"
                onClick={handleCancel}
                disabled={isCancelling}
              >
                <i className="fas fa-stop me-2" />
                {isCancelling ? t("Cancelando...") : t("Cancelar")}
              </Button>
            )}

            <Button
              variant="outline-secondary"
              size="lg"
              onClick={handleClearJobs}
              disabled={isClearing || isRunning}
              title={String(t("Limpia jobs terminados y zombis (>15 min sin avance)"))}
            >
              <i className="fas fa-broom me-2" />
              {isClearing ? t("Limpiando...") : t("Limpiar jobs")}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {status && (
        <Card>
          <Card.Header className="bg-light d-flex justify-content-between align-items-center">
            <strong>{t("4. Progreso")}</strong>
            <span className={`badge bg-${stageVariant(status.stage)}`}>{status.stage}</span>
          </Card.Header>
          <Card.Body>
            <ProgressBar
              now={status.progressPercent}
              label={`${status.progressPercent.toFixed(1)}%`}
              variant={stageVariant(status.stage)}
              animated={isRunning}
              striped={isRunning}
              style={{ height: 28 }}
              className="mb-3"
            />

            <Row className="g-3">
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Filas procesadas")}</small>
                <strong>{status.rowsProcessed.toLocaleString()}</strong>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("MB procesados")}</small>
                <strong>
                  {status.megabytesProcessed.toLocaleString()} / {status.fileSizeMB.toLocaleString()}
                </strong>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Velocidad")}</small>
                <strong>
                  {status.rowsPerSecond.toLocaleString()} {t("filas/s")}
                </strong>
                <br />
                <small>{status.megabytesPerSecond.toLocaleString()} MB/s</small>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Tiempo")}</small>
                <strong>{formatSeconds(status.elapsedSeconds)}</strong>
                <br />
                <small>
                  ETA: {isRunning ? formatSeconds(status.etaSeconds) : "—"}
                </small>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <small className="text-muted d-block">{t("Última línea procesada")}</small>
                <strong>{status.currentLine.toLocaleString()}</strong>
                <br />
                <small className="text-muted">
                  {t("Úsala en 'Reanudar desde línea' si necesitas continuar tras una interrupción")}
                </small>
              </Col>
              {(status.skippedRows ?? 0) > 0 && (
                <Col md={6}>
                  <small className="text-muted d-block">{t("Filas corruptas omitidas")}</small>
                  <strong className="text-warning">
                    {status.skippedRows.toLocaleString()}
                  </strong>
                </Col>
              )}
            </Row>

            {status.errorMessage && (
              <div className="alert alert-danger mt-3 mb-0">
                <strong>{t("Error")}:</strong> {status.errorMessage}
              </div>
            )}
            {status.message && status.stage === "Completed" && (
              <div className="alert alert-success mt-3 mb-0">
                <i className="fas fa-check-circle me-2" />
                {status.message}
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default AdminSalariosComponent
